import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { CommunityLockerRoomSummary } from '@/types/community';

interface LockerRoomCardProps {
  lockerRoom: CommunityLockerRoomSummary;
  className?: string;
}

const themeStyleClasses: Record<string, { bg: string; accent: string; border: string }> = {
  electric: { bg: 'bg-gradient-to-br from-amber-50 to-fuchsia-50', accent: 'bg-indigo-600', border: 'border-indigo-200' },
  earth: { bg: 'bg-gradient-to-br from-emerald-50 to-amber-50', accent: 'bg-emerald-700', border: 'border-emerald-200' },
  midnight: { bg: 'bg-gray-900', accent: 'bg-amber-500', border: 'border-amber-400' },
  clean: { bg: 'bg-white', accent: 'bg-blue-600', border: 'border-blue-200' },
};

function getInitials(name: string) {
  const value = (name || '').trim();
  if (!value) return 'LR';
  const parts = value.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

export default function LockerRoomCard({ lockerRoom, className = '' }: LockerRoomCardProps) {
  const themeStyle = themeStyleClasses[lockerRoom.theme_style] || themeStyleClasses.electric;
  const displayName = lockerRoom.display_name || lockerRoom.locker_room_name;
  const initials = useMemo(() => getInitials(displayName), [displayName]);
  const profileUrl = lockerRoom.space_code ? `/locker/${lockerRoom.space_code}` : '#';
  const accentColor = lockerRoom.accent_color || '#4f46e5';

  return (
    <Link
      to={profileUrl}
      className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border ${themeStyle.border} bg-white/90 shadow-soft transition hover:-translate-y-0.5 hover:shadow-md ${className}`.trim()}
    >
      {/* Header accent */}
      <div className="h-2 w-full" style={{ backgroundColor: accentColor }} />

      <div className="relative flex items-center gap-3 p-4">
        {lockerRoom.coach_avatar ? (
          <img
            src={lockerRoom.coach_avatar}
            alt={lockerRoom.coach_name}
            className="h-14 w-14 rounded-full border-2 border-white bg-white object-cover shadow-sm"
            loading="lazy"
          />
        ) : (
          <div
            className="flex h-14 w-14 items-center justify-center rounded-full text-white"
            style={{ backgroundColor: accentColor }}
          >
            <span className="text-lg font-semibold">{initials}</span>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-base font-semibold text-cocoa group-hover:text-cocoa/80 line-clamp-1">
            {displayName}
          </p>
          <p className="mt-0.5 text-sm text-cocoa/70 line-clamp-1">{lockerRoom.coach_name}</p>
          {lockerRoom.roster_count > 0 && (
            <span
              className="mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium text-white"
              style={{ backgroundColor: accentColor }}
            >
              {lockerRoom.roster_count} athlete{lockerRoom.roster_count !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {lockerRoom.about && (
        <div className="px-4 pb-2">
          <p className="text-sm text-cocoa/70 line-clamp-2">{lockerRoom.about}</p>
        </div>
      )}

      <div className="mt-auto border-t border-sand/60 bg-sand/20 p-3">
        <div className="flex items-center justify-between text-xs text-cocoa/70">
          <span className="flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Locker Room
          </span>
          {lockerRoom.updated_at && (
            <span>Updated {new Date(lockerRoom.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
          )}
        </div>
      </div>
    </Link>
  );
}
